import type { QueueEntry, QueueRoute, QueueSnapshot } from "../../shared/global-queue";
import type { FixtureDescriptor } from "./fixtures";

function routeFor(fixture: FixtureDescriptor): QueueRoute[] {
  const reason = fixture.assignment?.reason;
  if (reason === "requested-reviewer") return [{ type: "reviewer", target: "you", precedence: 1 }];
  if (reason === "requested-team") return [{ type: "team", target: "engineering", precedence: 2 }];
  if (reason === "assignee") return [{ type: "assignee", target: "you", precedence: 3 }];
  return [{ type: "pool", target: "general", precedence: 4 }];
}

function entryFor(fixture: FixtureDescriptor, position: number, observedAt: string): QueueEntry {
  const routes = routeFor(fixture);
  const explicit = routes[0]?.type !== "pool";
  const claim = fixture.claim?.status === "held" ? {
    status: "other" as const,
    reviewerLogin: fixture.claim.reviewerLogin ?? "another-reviewer",
    expiresAt: fixture.claim.expiresAt ?? new Date(Date.now() + 30 * 60_000).toISOString(),
  } : null;
  const gate = fixture.chewfile?.status === "required-missing" ? "chewfile-required"
    : fixture.chewfile?.status === "stale" ? "chewfile-stale"
      : fixture.chewfile?.status === "description" ? "guided"
      : fixture.chewfile?.status ?? "guided";
  const reviewable = fixture.reviewable !== false;
  const status = fixture.draft ? "draft"
    : !reviewable ? "blocked"
      : claim ? "in-review"
        : explicit ? "assigned"
          : "ready";
  const outcome = fixture.assignment?.reason === "author" || !reviewable || fixture.draft
    ? "ineligible" as const
    : explicit ? "assigned" as const : "available" as const;
  const reasonCode = fixture.draft ? "draft"
    : !reviewable ? gate === "chewfile-required" ? "chewfile-required" : "product-gate"
      : claim ? "claimed-by-another"
        : fixture.assignment?.reason ?? "general-pool";
  return {
    position,
    repository: fixture.repository,
    pullNumber: fixture.pullNumber,
    headSha: fixture.headSha ?? position.toString(16).padStart(40, "0"),
    title: fixture.title,
    url: fixture.url ?? null,
    reviewPath: fixture.reviewPath ?? `/review/${fixture.id}`,
    authorLogin: fixture.assignment?.reason === "author" ? "you" : `author-${fixture.pullNumber}`,
    waitSince: fixture.queue.waitingSince ?? new Date(Date.now() - position * 3_600_000).toISOString(),
    githubUpdatedAt: observedAt,
    observedAt,
    status,
    reasonCode,
    reasonDetail: fixture.queue.reason,
    lifecycle: fixture.draft ? "draft" : "open",
    reviewNeed: "needed",
    chewGate: gate,
    routing: fixture.assignment?.reason === "author" ? "author" : routes[0]?.type ?? "pool",
    coordination: claim ? "claimed" : "available",
    routes,
    activeReviewer: null,
    claim,
    guide: { origin: fixture.chewfile?.status === "description" ? "unguided" : gate === "guided" ? "authored" : gate === "unguided" ? "unguided" : null, importedAt: observedAt, staleAt: null },
    stackState: (fixture.scope.stack.reviewAfter?.length ?? 0) > 0 ? "blocked" : "standalone",
    sourceCompleteness: "complete",
    estimatedMinutes: fixture.queue.estimatedMinutes,
    difficulty: fixture.queue.difficulty,
    areas: fixture.queue.areas,
    stats: fixture.stats,
    attention: {
      ci: position % 4 === 1
        ? { state: "failure", total: 6, failed: 1, pending: 0 }
        : { state: "success", total: 6, failed: 0, pending: 0 },
      conversation: {
        issueComments: position % 3,
        reviewThreads: position % 2,
        unresolvedThreads: position % 4 === 1 ? 2 : 0,
        latest: position % 4 === 1 ? {
          author: "reviewer",
          body: "Please check the requested change before the next review pass.",
          createdAt: observedAt,
          url: fixture.url ?? `https://github.com/${fixture.repository}/pull/${fixture.pullNumber}`,
        } : null,
      },
    },
    forMe: {
      outcome,
      reasonCode,
      detail: outcome === "ineligible" ? "This row is visible, but it is not currently an opportunity for you." : "This row is available in the local operational-queue preview.",
      skipped: false,
      eligibleRank: outcome === "ineligible" ? null : position,
    },
  };
}

export function localGlobalQueueSnapshot(fixtures: FixtureDescriptor[]): QueueSnapshot {
  const observedAt = new Date().toISOString();
  const entries = fixtures.map((fixture, index) => entryFor(fixture, index + 1, observedAt));
  const count = (status: QueueEntry["status"]) => entries.filter((entry) => entry.status === status).length;
  return {
    source: "d1",
    generation: { id: "local-preview", computedAt: observedAt, stale: false },
    activeSyncRun: null,
    repositories: [...new Set(entries.map((entry) => entry.repository))].map((repository) => ({
      repository,
      status: "current",
      lastAttemptAt: observedAt,
      lastSuccessfulFullScanAt: observedAt,
      stale: false,
      errorCode: null,
      errorDetail: null,
    })),
    summary: {
      total: entries.length,
      ready: count("ready"),
      assigned: count("assigned"),
      inReview: count("in-review"),
      blocked: count("blocked"),
      satisfied: count("satisfied"),
      drafts: count("draft"),
      errors: count("error"),
    },
    entries,
    reviewerCoverage: [{
      reviewer: "you",
      assignments: entries.filter((entry) => entry.forMe.outcome === "assigned").length,
      activeWork: entries.filter((entry) => ["claimed-by-self", "in-review-by-self"].includes(entry.forMe.outcome)).length,
      availableOpportunities: entries.filter((entry) => entry.forMe.outcome === "available").length,
      zeroOpportunityReasons: entries.filter((entry) => entry.forMe.outcome === "ineligible")
        .reduce<Record<string, number>>((reasons, entry) => {
          reasons[entry.forMe.reasonCode] = (reasons[entry.forMe.reasonCode] ?? 0) + 1;
          return reasons;
        }, {}),
    }],
    warnings: [],
  };
}
